import { Box, Button, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useProduct } from "src/context/ProductProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import { productToBenefitsMap } from "./constants/productToBenefitsMap";
import { Check } from "@mui/icons-material";
import { theme } from "src/assets/theme";
import { MedicationIcon } from "src/shared/components/MedicationIcon";

const navigateToWelcome2 = [
  "st",
  "tr",
  "mt",
  "st-ot",
  "os-ot",
  "tr-ot",
  "fhl",
  "ed-ot",
  "amh-ot",
  "aa-ot",
  "ac-ot",
  "ro-ot",
  "bb-ot",
  "sl-ot",
  "iwl-ot",
  "wp-ot",
];

const displayMedicationIcon = ["aa-ot", "ac-ot", "ro-ot", "bb-ot", "sl-ot"];

const Welcome = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const productName = useProduct((p) => p.name);
  const productSlug = useProduct((p) => p.slug);
  const weightLossSlugs = useMemo(
    () =>
      ["st", "tr", "mt", "st-ot", "tr-ot", "wp-ot", "os-ot", "db-ot"].includes(
        productSlug,
      ),
    [productSlug],
  );

  const page = "welcome";
  const { t } = useTranslation();

  const handleNavigation = useCallback(() => {
    const next = navigateToWelcome2.includes(productSlug)
      ? "welcome-2"
      : "signup";

    navigate(pathname.replace("welcome", next));
  }, [navigate, pathname, productSlug]);

  const title = useMemo(() => {
    if (productName != null) {
      return t(`${page}.${productSlug}.title`, {
        product: productName.toLowerCase(),
      });
    }

    return "";
  }, [productName, productSlug, t]);

  const subText = useMemo(() => {
    if (productName != null) {
      if (!weightLossSlugs || productSlug === "db-ot") {
        return t(`${page}.${productSlug}.subtext`);
      }

      return t(`${page}.subtext`, { product: productName.toLowerCase() });
    }

    return "";
  }, [productName, productSlug, t, weightLossSlugs]);

  const disclaimer = useMemo(() => {
    if (["ed-ot", "amh-ot", "db-ot", "cv-ot"].includes(productSlug))
      return null;

    if (!weightLossSlugs) {
      return t(`${page}.${productSlug}.disclaimer`);
    }

    return t(`${page}.disclaimer`);
  }, [productSlug, t, weightLossSlugs]);

  const button = useMemo(() => {
    if (!weightLossSlugs) {
      return t(`${page}.${productSlug}.button`);
    }

    return t(`${page}.button`);
  }, [productSlug, t, weightLossSlugs]);

  const benefits = productToBenefitsMap[productSlug];

  return (
    <Stack gap={1}>
      <Stack gap={2}>
        <Typography variant={isMobile ? "h6" : "h4"} fontWeight={700}>
          {title}
        </Typography>
        <Typography variant={isMobile ? "subtitle2" : "subtitle1"}>
          {subText}
        </Typography>

        {displayMedicationIcon.includes(productSlug) ? (
          <MedicationIcon
            name={productName}
            slug={productSlug}
            width={214}
            height={208}
            sx={{ ml: "auto", mr: "auto" }}
          />
        ) : null}

        <Typography fontSize={isMobile ? 16 : 18} fontWeight={700}>
          {disclaimer}
        </Typography>

        {benefits ? (
          <Stack gap={1}>
            {benefits.header ? (
              <Typography
                variant="subtitle1"
                fontWeight={600}
                fontSize={"1.3rem"}
              >{`🔥 ${t(benefits.header)}`}</Typography>
            ) : null}
            <Box>
              {benefits.items.map((item) => (
                <Box key={item} display="flex" gap={1} alignItems="center">
                  <Check sx={{ color: theme.palette.primary.main }} />
                  <Typography variant={isMobile ? "subtitle2" : "subtitle1"}>
                    {t(item)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        ) : null}
      </Stack>
      <Button
        fullWidth
        variant="contained"
        sx={{ mt: 3 }}
        onClick={handleNavigation}
        disabled={!productName}
      >
        {button}
      </Button>
    </Stack>
  );
};

export default Welcome;
